// src/layouts/MainLayout.jsx
import React, { useState, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Drawer, Grid } from 'antd';
import {
    DashboardOutlined,
    SwapOutlined,

    ShareAltOutlined,
    WalletOutlined,
    BarChartOutlined,
    FileTextOutlined,
    SettingOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
    ApiOutlined,
    BellOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import TopNav from '../components/layout/TopNav';
import SiteFooter from '../components/layout/SiteFooter';
import { ROUTES } from '../constants/routes';

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export default function MainLayout() {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { user } = useAuth();
    const { isDark, isRtl } = useTheme();

    /* ------------------------- MENU ------------------------- */
    const menuItems = useMemo(
        () => [
            {
                key: ROUTES.DASHBOARD,
                icon: <DashboardOutlined />,
                label: <Link to={ROUTES.DASHBOARD}>Dashboard</Link>,
            },
            {
                key: ROUTES.MARKET,
                icon: <BarChartOutlined />,
                label: <Link to={ROUTES.MARKET}>Market</Link>,
            },
            {
                key: ROUTES.WALLETS,
                icon: <WalletOutlined />,
                label: <Link to={ROUTES.WALLETS}>Wallets</Link>,
            },
            {
                key: 'orders',
                icon: <FileTextOutlined />,
                label: 'Orders',
                children: [
                    { key: ROUTES.OPEN_ORDERS, label: <Link to={ROUTES.OPEN_ORDERS}>Open Orders</Link> },
                    { key: ROUTES.ORDER_HISTORY, label: <Link to={ROUTES.ORDER_HISTORY}>Order History</Link> },
                    { key: ROUTES.TRANSACTIONS, label: <Link to={ROUTES.TRANSACTIONS}>Transactions</Link> },
                    { key: ROUTES.INVOICES, label: <Link to={ROUTES.INVOICES}>Invoices</Link> },
                ],
            },
            {
                key: ROUTES.KYC,
                icon: <SafetyCertificateOutlined />,
                label: <Link to={ROUTES.KYC}>KYC</Link>,
            },
            {
                key: 'settings',
                icon: <SettingOutlined />,
                label: 'Settings',
                children: [
                    { key: ROUTES.PROFILE, icon: <UserOutlined />, label: <Link to={ROUTES.PROFILE}>Profile</Link> },
                    { key: ROUTES.SECURITY, icon: <SafetyCertificateOutlined />, label: <Link to={ROUTES.SECURITY}>Security</Link> },
                    { key: ROUTES.API_KEYS, icon: <ApiOutlined />, label: <Link to={ROUTES.API_KEYS}>API Keys</Link> },
                    { key: ROUTES.NOTIFICATIONS, icon: <BellOutlined />, label: <Link to={ROUTES.NOTIFICATIONS}>Notifications</Link> },
                    { key: ROUTES.PREFERENCES, icon: <GlobalOutlined />, label: <Link to={ROUTES.PREFERENCES}>Preferences</Link> },
                ],
            },
            {
                key: ROUTES.REFERRALS,
                icon: <ShareAltOutlined />,
                label: <Link to={ROUTES.REFERRALS}><FormattedMessage id="ref.title" /></Link>,
            },
        ],
        []
    );

    /* ------------------------- BRAND ------------------------- */
    const Brand = ({ collapsed: isCollapsed }) => (
        <div
            style={{
                height: 56,
                margin: '16px 16px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: 10,
                color: isDark ? '#fff' : '#0b1220',
                fontWeight: 800,
                fontSize: 20,
                letterSpacing: 1,
                transition: 'all 0.2s',
            }}
        >
            <div
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #1677ff, #4096ff)',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    fontSize: 16,
                    flexShrink: 0,
                }}
            >
                S
            </div>
            {!isCollapsed && (
                <span>
                    SOHO<span style={{ color: '#1677ff' }}>CARD</span>
                </span>
            )}
        </div>
    );

    /* ------------------------- SIDER ------------------------- */
    const siderContent = (
        <>
            <Brand collapsed={collapsed && !isMobile} />
            <Menu
                theme={isDark ? 'dark' : 'light'}
                mode="inline"
                selectedKeys={[location.pathname]}
                defaultOpenKeys={['orders', 'settings']}
                items={menuItems}
                style={{ borderInlineEnd: 'none', background: 'transparent' }}
                onClick={() => isMobile && setMobileOpen(false)}
            />
        </>
    );

    /* ------------------------- RENDER ------------------------ */
    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* Fixed transparent top nav */}
            <TopNav onOpenSidebar={() => setMobileOpen(true)} />

            {/* Desktop sider */}
            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    theme={isDark ? 'dark' : 'light'}
                    width={240}
                    collapsedWidth={72}
                    style={{
                        background: isDark ? '#0d1424' : '#fff',
                        borderInlineEnd: `1px solid ${isDark ? '#1f2937' : '#eaeef5'}`,
                        position: 'fixed',
                        top: 72,                              // below the fixed TopNav
                        insetInlineStart: 0,
                        height: 'calc(100vh - 72px)',
                        overflow: 'auto',
                    }}
                >
                    {siderContent}
                </Sider>
            )}

            {/* Mobile drawer */}
            {isMobile && (
                <Drawer
                    placement={isRtl ? 'right' : 'left'}
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    closable={false}
                    width={260}
                    styles={{
                        body: {
                            padding: 0,
                            background: isDark ? '#0d1424' : '#fff',
                        },
                    }}
                >
                    {siderContent}
                </Drawer>
            )}

            {/* Main area — offset by sider width on desktop, padding-top for TopNav */}
            <Layout
                style={{
                    marginInlineStart: isMobile ? 0 : (collapsed ? 72 : 240),
                    paddingTop: 72,
                    transition: 'margin-inline-start 0.2s',
                }}
            >
                <Content
                    style={{
                        margin: isMobile ? 12 : 24,
                        padding: 0,
                        minHeight: 'calc(100vh - 200px)',
                    }}
                >
                    <Outlet />
                </Content>

                <SiteFooter />
            </Layout>
        </Layout>
    );
}